// src/firebase/connection.ts
import { 
  enableNetwork, 
  disableNetwork, 
  waitForPendingWrites,
  clearIndexedDbPersistence 
} from 'firebase/firestore';
import db from './firestore';

export class FirestoreConnectionManager {
  private static instance: FirestoreConnectionManager;
  private isOnline = true;
  private retryCount = 0;
  private maxRetries = 3;

  private constructor() {}

  static getInstance(): FirestoreConnectionManager {
    if (!FirestoreConnectionManager.instance) {
      FirestoreConnectionManager.instance = new FirestoreConnectionManager();
    }
    return FirestoreConnectionManager.instance;
  }

  async handleConnectionError(error: any): Promise<boolean> {
    void error;
    
    // Don't retry permission errors - these need authentication/rule fixes
    if (error?.code === 'permission-denied') {
      return false;
    }
    
    // Check if it's a network-related error
    if (this.isNetworkError(error)) {
      return this.attemptReconnection();
    }
    
    return false;
  }

  private isNetworkError(error: any): boolean {
    const networkErrors = [
      'unavailable',
      'deadline-exceeded', 
      'cancelled',
      'failed-precondition'
    ];
    
    return networkErrors.some(code => 
      error?.code === code || 
      error?.message?.includes('fetch') ||
      error?.message?.includes('network') ||
      error?.message?.includes('timeout')
    );
  }

  private async attemptReconnection(): Promise<boolean> {
    if (this.retryCount >= this.maxRetries) {
      return false;
    }

    this.retryCount++;

    try {
      // Disable network first
      await disableNetwork(db);
      
      // Wait a bit
      await new Promise(resolve => setTimeout(resolve, 1000 * this.retryCount));
      
      // Re-enable network
      await enableNetwork(db);
      
      // Wait for any pending writes
      await waitForPendingWrites(db);
      
      this.retryCount = 0; // Reset on successful reconnection
      this.isOnline = true;
      return true;
      
    } catch (reconnectError) {
      void reconnectError;
      this.isOnline = false;
      return false;
    }
  }

  async forceOfflineMode(): Promise<void> {
    try {
      await disableNetwork(db);
      this.isOnline = false;
    } catch (error) {
      void error;
    }
  }

  async forceOnlineMode(): Promise<void> {
    try {
      await enableNetwork(db);
      this.isOnline = true;
    } catch (error) {
      void error;
    }
  }

  async clearOfflineData(): Promise<void> {
    try {
      await disableNetwork(db);
      await clearIndexedDbPersistence(db);
      await enableNetwork(db);
    } catch (error) {
      void error;
    }
  }

  getConnectionStatus(): boolean {
    return this.isOnline;
  }

  resetRetryCount(): void {
    this.retryCount = 0;
  }
}

// Helper function to wrap Firestore operations with retry logic
export async function withRetry<T>(
  operation: () => Promise<T>,
  fallback?: T
): Promise<T> {
  const connectionManager = FirestoreConnectionManager.getInstance();
  
  try {
    return await operation();
  } catch (error) {
    const reconnected = await connectionManager.handleConnectionError(error);
    
    if (reconnected) {
      try {
        return await operation();
      } catch (retryError) {
        void retryError;
        if (fallback !== undefined) return fallback;
        throw retryError;
      }
    }
    
    if (fallback !== undefined) return fallback;
    throw error;
  }
}